/*
  ─── USAGE ───────────────────────────────────────────────────────────────────
  import { PrintBill, usePrintBill } from './PrintBill';

  const { printRef, thermalRef, triggerPrint, triggerThermal } = usePrintBill();

  <PrintBill ref={printRef} thermalRef={thermalRef} order={order} />

  <button className="btn" onClick={triggerPrint}><PrintIcon /> Print Bill</button>
  <button className="btn" onClick={triggerThermal}><FileIcon /> Thermal Bill</button>
  ─────────────────────────────────────────────────────────────────────────────
*/

import React, { forwardRef, useCallback, useRef } from "react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

const fmt = (v) => v != null ? `Rs.${Number(v).toFixed(2)}` : "—";

const STATUS_MAP = {
    1:  { label: "Pending"    },
    2:  { label: "Accepted"   },
    3:  { label: "Preparing"  },
    4:  { label: "On the Way" },
    5:  { label: "Delivered"  },
    6:  { label: "Cancelled"  },
    8:  { label: "Ready"      },
    10: { label: "Picked Up"  },
    11: { label: "Completed"  },
};

const openPrint = (html, pageSize = "A4") => {
    const win = window.open("", "_blank", "width=800,height=900");
    win.document.write(`<!DOCTYPE html><html><head>
        <meta charset="utf-8"/>
        <title>Order Bill</title>
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { background: #fff; }
            @page { size: ${pageSize}; margin: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
    </head><body>${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export const usePrintBill = () => {
    const printRef   = useRef(null);
    const thermalRef = useRef(null);

    const triggerPrint = useCallback(() => {
        if (printRef.current) openPrint(printRef.current.innerHTML, "A4");
    }, []);

    const triggerThermal = useCallback(() => {
        const el = thermalRef.current;
        if (!el) return;
        const win = window.open("", "_blank", "width=420,height=700");
        win.document.write(`<!DOCTYPE html><html><head>
            <meta charset="utf-8"/>
            <title>Thermal Bill</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { background: #f1f5f9; font-family: Arial, sans-serif; padding: 20px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
                .print-btn { display: block; width: 100%; max-width: 320px; padding: 12px; background: #6366f1; color: #fff; border: none; border-radius: 8px; font-size: 15px; font-weight: 700; cursor: pointer; text-align: center; letter-spacing: 0.03em; }
                .print-btn:hover { background: #4f46e5; }
                @media print { .print-btn { display: none; } @page { size: 80mm auto; margin: 0; } body { background: #fff; padding: 0; } }
            </style>
        </head><body>
            <button class="print-btn" onclick="window.print()">🖨️ Print Thermal Bill</button>
            ${el.innerHTML}
        </body></html>`);
        win.document.close();
    }, []);

    return { printRef, thermalRef, triggerPrint, triggerThermal };
};

// ─────────────────────────────────────────────────────────────────────────────
// A4 PRINT BILL
// ─────────────────────────────────────────────────────────────────────────────
export const PrintBill = forwardRef(({ order, thermalRef }, ref) => {
    if (!order) return null;

    const status   = STATUS_MAP[order?.orderstatus_id] || { label: "Unknown" };
    const isPickup = order?.delivery_type !== 1;
    const items    = order?.orderitems || [];

    const subtotal       = items.reduce((s, i) => s + (Number(i.price) * Number(i.quantity || 1)), 0);
    const deliveryCharge = Number(order?.delivery_charge || 0);
    const discount       = Number(order?.discount || 0);
    const tax            = Number(order?.tax || 0);
    const total          = Number(order?.total_amount || (subtotal + deliveryCharge + tax - discount));

    return (
        <>
            {/* ── A4 Bill (hidden) ── */}
            <div ref={ref} style={{ display: "none" }}>
                <div style={{
                    width: "210mm", minHeight: "297mm", padding: "12mm 14mm",
                    background: "#fff", fontFamily: "'Segoe UI', Arial, sans-serif",
                    fontSize: "13px", color: "#1a1a1a", lineHeight: 1.5,
                }}>
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingBottom: "10mm", borderBottom: "2px solid #1a1a1a" }}>
                        <div>
                            <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px" }}>{order?.restaurant?.name || "Restaurant"}</div>
                            {order?.restaurant?.address && <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px", maxWidth: "260px" }}>{order.restaurant.address}</div>}
                            {order?.restaurant?.phone  && <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>Ph: {order.restaurant.phone}</div>}
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Tax Invoice</div>
                            <div style={{ fontSize: "20px", fontWeight: 800, color: "#6366f1", marginTop: "2px" }}>#{order?.unique_order_id}</div>
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>{formatDate(order?.created_at)}</div>
                            <div style={{ display: "inline-block", marginTop: "6px", padding: "3px 10px", borderRadius: "20px", border: "1.5px solid #1a1a1a", fontSize: "11px", fontWeight: 700 }}>
                                {status.label}
                            </div>
                        </div>
                    </div>

                    {/* Customer + Order Info */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6mm", padding: "8mm 0", borderBottom: "1px dashed #e2e8f0" }}>
                        <div>
                            <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "5px" }}>Bill To</div>
                            <div style={{ fontWeight: 700, fontSize: "14px" }}>{order?.user?.name || "—"}</div>
                            {order?.user?.phone && <div style={{ fontSize: "12px", color: "#475569", marginTop: "2px" }}>Ph: {order.user.phone}</div>}
                            {order?.user?.email && <div style={{ fontSize: "12px", color: "#475569", marginTop: "2px" }}>{order.user.email}</div>}
                            {!isPickup && order?.address && <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", lineHeight: 1.4 }}>{order.address}</div>}
                        </div>
                        <div>
                            <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "5px" }}>Order Info</div>
                            {[
                                ["Type",    isPickup ? "Self Pickup" : "Delivery"],
                                ["Payment", order?.payment_mode || "—"],
                                ...(order?.cancel_reason ? [["Reason", order.cancel_reason]] : []),
                            ].map(([label, value]) => (
                                <div key={label} style={{ display: "flex", gap: "6px", marginBottom: "3px", fontSize: "12px" }}>
                                    <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: "70px" }}>{label}:</span>
                                    <span style={{ fontWeight: 500 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Items Table */}
                    <div style={{ paddingTop: "6mm" }}>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "5px" }}>Order Items</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 80px 80px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px 8px 0 0", padding: "7px 12px", fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                            <div>Item</div><div style={{ textAlign: "center" }}>Qty</div><div style={{ textAlign: "right" }}>Price</div><div style={{ textAlign: "right" }}>Subtotal</div>
                        </div>
                        {items.map((item, idx) => {
                            const qty = Number(item?.quantity || 1);
                            const price = Number(item?.price || 0);
                            const isLast = idx === items.length - 1;
                            return (
                                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 60px 80px 80px", padding: "8px 12px", fontSize: "12px", borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0", borderBottom: isLast ? "1px solid #e2e8f0" : "1px dashed #f1f5f9", borderRadius: isLast ? "0 0 8px 8px" : 0, background: idx % 2 === 0 ? "#fff" : "#fafbfc", alignItems: "center" }}>
                                    <div style={{ fontWeight: 600 }}>{item?.name || "Item"}</div>
                                    <div style={{ textAlign: "center", color: "#6366f1", fontWeight: 700 }}>x{qty}</div>
                                    <div style={{ textAlign: "right", color: "#475569" }}>{fmt(price)}</div>
                                    <div style={{ textAlign: "right", fontWeight: 700 }}>{fmt(qty * price)}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Price Summary */}
                    <div style={{ marginTop: "6mm", marginLeft: "auto", width: "55%", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                        {[
                            { label: "Subtotal",        value: fmt(subtotal) },
                            ...(deliveryCharge > 0 ? [{ label: "Delivery",      value: fmt(deliveryCharge) }] : []),
                            ...(tax > 0            ? [{ label: "Tax",           value: fmt(tax) }] : []),
                            ...(discount > 0       ? [{ label: "Discount",      value: `- ${fmt(discount)}`, red: true }] : []),
                        ].map(({ label, value, red }) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 14px", fontSize: "12px", borderBottom: "1px dashed #f1f5f9", color: red ? "#ef4444" : "#475569" }}>
                                <span>{label}</span><span style={{ fontWeight: 600 }}>{value}</span>
                            </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 14px", background: "#0f172a" }}>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>Total</span>
                            <span style={{ fontSize: "17px", fontWeight: 800, color: "#fff" }}>{fmt(total)}</span>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ marginTop: "10mm", paddingTop: "5mm", borderTop: "1px dashed #e2e8f0", display: "flex", justifyContent: "space-between" }}>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>Thank you for your order!</div>
                        <div style={{ fontSize: "10px", color: "#cbd5e1", fontStyle: "italic" }}>Printed on {new Date().toLocaleString("en-IN")}</div>
                    </div>
                </div>
            </div>

            {/* ── Thermal Bill (hidden) ── */}
            <div ref={thermalRef} style={{ display: "none" }}>
                <div style={{
                    width: "80mm",
                    padding: "4mm 5mm 8mm",
                    background: "#fff",
                    fontFamily: "'Courier New', Courier, monospace",
                    fontSize: "11px",
                    color: "#000",
                    lineHeight: 1.4,
                }}>
                    {/* Restaurant name */}
                    <div style={{ textAlign: "center", fontWeight: 700, fontSize: "15px", letterSpacing: "0.5px" }}>
                        {order?.restaurant?.name || "Restaurant"}
                    </div>
                    {order?.restaurant?.address && (
                        <div style={{ textAlign: "center", fontSize: "9px", color: "#333", marginTop: "2px" }}>
                            {order.restaurant.address}
                        </div>
                    )}
                    {order?.restaurant?.phone && (
                        <div style={{ textAlign: "center", fontSize: "9px", color: "#333" }}>
                            Ph: {order.restaurant.phone}
                        </div>
                    )}

                    {/* Dashed separator */}
                    <div style={{ borderTop: "1px dashed #000", margin: "4mm 0 3mm" }} />

                    {/* Order meta */}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "2px" }}>
                        <span style={{ fontWeight: 700 }}>#{order?.unique_order_id}</span>
                        <span>{status.label}</span>
                    </div>
                    <div style={{ fontSize: "9px", color: "#444", marginBottom: "1px" }}>
                        {formatDate(order?.created_at)}
                    </div>
                    <div style={{ fontSize: "9px", color: "#444", marginBottom: "1px" }}>
                        Type: {isPickup ? "Self Pickup" : "Delivery"} &nbsp;|&nbsp; {order?.payment_mode || "—"}
                    </div>

                    {/* Dashed separator */}
                    <div style={{ borderTop: "1px dashed #000", margin: "3mm 0 2mm" }} />

                    {/* Customer */}
                    <div style={{ fontSize: "10px", fontWeight: 700, marginBottom: "1px" }}>
                        {order?.user?.name || "—"}
                    </div>
                    {order?.user?.phone && (
                        <div style={{ fontSize: "9px", color: "#333" }}>Ph: {order.user.phone}</div>
                    )}
                    {!isPickup && order?.address && (
                        <div style={{ fontSize: "9px", color: "#333", marginTop: "1px", lineHeight: 1.3 }}>
                            {order.address}
                        </div>
                    )}

                    {/* Dashed separator */}
                    <div style={{ borderTop: "1px dashed #000", margin: "3mm 0 2mm" }} />

                    {/* Items header */}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2mm" }}>
                        <span style={{ flex: 1 }}>Item</span>
                        <span style={{ width: "20px", textAlign: "center" }}>Qty</span>
                        <span style={{ width: "50px", textAlign: "right" }}>Amt</span>
                    </div>

                    {/* Items */}
                    {items.map((item, idx) => {
                        const qty   = Number(item?.quantity || 1);
                        const price = Number(item?.price || 0);
                        return (
                            <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "2px", alignItems: "flex-start" }}>
                                <span style={{ flex: 1, paddingRight: "4px", lineHeight: 1.3 }}>{item?.name || "Item"}</span>
                                <span style={{ width: "20px", textAlign: "center" }}>{qty}</span>
                                <span style={{ width: "50px", textAlign: "right", fontWeight: 600 }}>{fmt(qty * price)}</span>
                            </div>
                        );
                    })}

                    {/* Dashed separator */}
                    <div style={{ borderTop: "1px dashed #000", margin: "3mm 0 2mm" }} />

                    {/* Price rows */}
                    {[
                        { label: "Subtotal",  value: fmt(subtotal) },
                        ...(deliveryCharge > 0 ? [{ label: "Delivery", value: fmt(deliveryCharge) }] : []),
                        ...(tax > 0            ? [{ label: "Tax",      value: fmt(tax) }] : []),
                        ...(discount > 0       ? [{ label: "Discount", value: `- ${fmt(discount)}` }] : []),
                    ].map(({ label, value }) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "1px" }}>
                            <span style={{ color: "#555" }}>{label}</span>
                            <span>{value}</span>
                        </div>
                    ))}

                    {/* Total */}
                    <div style={{ borderTop: "2px solid #000", marginTop: "2mm", paddingTop: "2mm", display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 700 }}>
                        <span>TOTAL</span>
                        <span>{fmt(total)}</span>
                    </div>

                    {/* Footer */}
                    <div style={{ borderTop: "1px dashed #000", margin: "4mm 0 2mm" }} />
                    <div style={{ textAlign: "center", fontSize: "9px", color: "#555" }}>
                        Thank you for your order!
                    </div>
                    <div style={{ textAlign: "center", fontSize: "8px", color: "#aaa", marginTop: "2px" }}>
                        {new Date().toLocaleString("en-IN")}
                    </div>
                </div>
            </div>
        </>
    );
});

PrintBill.displayName = "PrintBill";